<upload-source-picker>

    <!-- Only the repository column waits for the repository list. Both used to: the
         whole row sat behind if="{state.integrations}" and a full-width skeleton stood
         in for it, so a slow /repository-integrations hid the upload box — the reason
         the page exists — behind a placeholder for data the upload box never reads.
         Dropping a file works immediately now. -->
    <div class="w-full flex flex-col md:flex-row h-auto gap-12">

        <div class="w-full {leftColumnWidth()} items-start">
            <h3 class="text-md font-bold text-center mb-2 mx-auto px-8 text-accent">{leftHeading()}</h3>
            <upload-box
                    disabled="{props.disabled}"
                    disabled-reason="{props.disabledreason}"
                    collectionid="{props.collectionid}"
                    resourceid="{props.resourceid}"
                    onstart="{props.onhandoff}"
                    onupdated="{handleLocalUpload}"></upload-box>
        </div>

        <div class="w-full md:w-1/2 flex flex-col justify-start items-center"
             if="{!state.integrations}" role="status" aria-label="{locale?.t('loading')}">
            <div class="skeleton h-48 w-full"></div>
        </div>

        <div class="w-full md:w-1/2 flex flex-col justify-start items-center" if="{state.integrations && state.integrations.length}">
            <div class="w-full flex items-center justify-center gap-2 mb-2 px-8">
                <h3 class="text-md font-bold text-accent">{rightHeading()}</h3>
                <!-- Discoverability link to the workspace's
                     Content Repositories Configuration page.
                     Admin-only because that page is bundle-gated to
                     CUSTOMER_ADMIN+ — a non-admin click would land
                     on a not-found, so we hide it for them. -->
                <a if="{isAdmin()}"
                   href="javascript:"
                   onclick="{() => tools?.navigateTo('content-repositories')}"
                   class="text-xs link link-primary link-hover whitespace-nowrap">
                    <i class="icon-metalark-settings me-1"></i>{locale?.t('upload_configurerepositories')}
                </a>
            </div>
            <div class="w-full flex flex-row flex-wrap justify-center items-start" if="{state.integrations && state.integrations.length}">
                <div class="w-full lg:w-1/2 px-2 pb-2" each="{integration in state.integrations}" key="{integration.repositoryId}">
                    <upload-repository
                            integration="{integration}"
                            onconnect="{handleOAuthComplete}"
                            onbrowse="{browseFiles}"
                            disablebrowse="{props.disabled}"
                            disabled-reason="{props.disabledreason}"
                    ></upload-repository>
                </div>
            </div>
        </div>

    </div>

    <!-- No-repositories nudge for admins. The right-side panel
         above only renders when integrations.length > 0, so on a
         fresh customer with no connectors there's nothing to
         surface the existence of the feature. Drop a single line
         under the upload box so an admin discovers the
         configuration page; non-admins keep the simpler "just
         upload" experience. -->
    <div if="{state.integrations && !state.integrations.length && isAdmin()}"
         class="w-full text-center text-sm text-base-content/80 mt-4">
        <a href="javascript:" onclick="{() => tools?.navigateTo('content-repositories')}"
           class="link link-primary link-hover">
            <i class="icon-metalark-link me-1"></i>{locale?.t('upload_connectrepository')}
        </a>
    </div>

    <script>
        import uploadBox from "./upload-box.tag"
        import uploadRepository from "./upload-repository.tag"
        import { getRepositoryService, getModalService, getApi } from '../services/service-loader';
        import { isTargetOnlyProvider } from '../constants/RepositoryProviderConstants';

        export default {
            get components() {
                return {
                    uploadBox,
                    uploadRepository
                };
            },

            state: {
                userContext: null,
                integrations: null
            },

            async onMounted() {
                await this.loadUserContext();
                await this.getRepositories();
            },

            async loadUserContext() {
                try {
                    const userContext = await this.tools.getUserContext();
                    this.update({ userContext });
                } catch (error) {
                    console.error('Failed to load user context for repository picker:', error);
                    this.update({ userContext: {} });
                }
            },

            // Gates the "Configure repositories" / "Connect a repository"
            // links that point at the bundle-protected
            // /content-repositories page. Surfacing them to non-admins
            // would just land them on a not-found.
            isAdmin() {
                const role = this.state.userContext?.role;
                return role === 'SYSTEM_ADMIN' || role === 'CUSTOMER_ADMIN';
            },

            // Column headings; a host (e.g. the reupload modal) can override them.
            // Guarded for the brief mount window before locale is injected.
            leftHeading() {
                return this.props.leftheading || this.locale?.t('upload_newcontent') || '';
            },

            rightHeading() {
                return this.props.rightheading || this.locale?.t('upload_importfromrepository') || '';
            },

            /**
             * Half width while the repository column is loading or populated, full width
             * only once we know there is nothing to sit beside. Reserving the half up
             * front means the common case — repositories exist — settles into its final
             * layout instead of reflowing when the fetch lands.
             */
            leftColumnWidth() {
                return (!this.state.integrations || this.state.integrations.length) ? 'md:w-1/2' : '';
            },

            async getRepositories(skipLoading = false) {
                try {
                    const repositoryService = await getRepositoryService();
                    const response = await repositoryService.getAll(skipLoading);
                    // Destination-only connections are excluded. They cannot be browsed,
                    // so listing one here gives a Browse button that fails — and the more
                    // recognisable the card looks, the more convincing the dead end.
                    // METALARK-1168 makes Moodle browsable, at which point it drops out of
                    // TARGET_ONLY_PROVIDERS and starts appearing here on its own.
                    this.state.integrations = (response.repositories || [])
                        .filter(r => !isTargetOnlyProvider(r?.provider));
                } catch (error) {
                    console.error('Failed to load repositories:', error);
                    this.state.integrations = [];
                }
                this.update();
            },

            async handleOAuthComplete(integration) {
                // OAuth completed - refresh repositories
                await this.getRepositories();
            },

            // upload-box finished a local upload batch. Bubble up so the host
            // can refresh its own view (job list, collection resources, etc.).
            handleLocalUpload() {
                if (this.props.oncomplete) this.props.oncomplete();
            },

            async browseFiles(integration) {
                try {
                    // Let a host (e.g. the Upload to Collection modal) step aside
                    // so the file picker isn't stacked on top of it.
                    if (this.props.onhandoff) this.props.onhandoff();
                    // Use the globally-mounted file-picker-modal. Scope it: pin
                    // the collection (collection modal) or cap selection to one
                    // file (reupload modal), otherwise open unrestricted.
                    const options = {};
                    if (this.props.collectionid) {
                        options.presetCollectionId = this.props.collectionid;
                        options.lockCollection = true;
                    }
                    if (this.props.resourceid) {
                        // Reupload reingests into one existing resource: a single
                        // file, and no collection.
                        options.maxFiles = 1;
                        options.hideCollection = true;
                    }
                    // If the host stepped aside for the picker (onhandoff), it
                    // hid itself. When the picker is dismissed without a
                    // selection, let the host restore itself instead of leaving
                    // the user with nothing open.
                    if (this.props.onhandoffcancel) {
                        options.onCancel = () => this.props.onhandoffcancel();
                    }
                    window.filePickerModal.show(
                        integration.repositoryId,
                        integration.provider,
                        integration.displayName,
                        (data) => this.onFilesSelected(data, integration),
                        Object.keys(options).length ? options : undefined
                    );
                } catch (error) {
                    console.error('Failed to open file picker:', error);
                    const modalService = await getModalService();
                    modalService.show(
                        this.locale.t('upload_pickerfailed'),
                        this.locale.t('upload_failedtoopenpicker') + ' ' + (error.response?.data?.error?.message || error.message)
                    );
                }
            },

            async onFilesSelected(data, integration) {
                const { fileIds, count, totalSize, providerContext, collectionId } = data;

                try {
                    const modalService = await getModalService();
                    const repositoryService = await getRepositoryService();

                    if (!integration) {
                        modalService.show(
                            this.locale.t('upload_discoveryfailed'),
                            this.locale.t('upload_repositoryinfomissing')
                        );
                        return;
                    }

                    //Discover files
                    const response = await repositoryService.discoverFiles(
                        integration.repositoryId,
                        {
                            selectedFileIds: fileIds,
                            totalSize: totalSize,
                            providerContext: providerContext || {}
                        }
                    );

                    // Wait for sync job to complete before approving
                    if (response.syncJobId) {
                        console.log('Waiting for sync job to complete:', response.syncJobId);
                        await this.pollSyncJobUntilComplete(integration.repositoryId, response.syncJobId);
                    }

                    // Extract file ID mapping (externalFileId -> database UUID)
                    const fileIdMapping = response.fileIdMapping || {};

                    // Reupload targets a single existing resource: ignore the
                    // collection and cap at one file. Otherwise prefer the
                    // collection the picker resolved, falling back to the one
                    // this picker is scoped to (collection modal).
                    const targetResourceId = this.props.resourceid || null;
                    const effectiveCollectionId = targetResourceId
                        ? null
                        : (collectionId || this.props.collectionid || null);
                    const idsToApprove = targetResourceId ? fileIds.slice(0, 1) : fileIds;

                    //Ingest/approve files using database UUIDs
                    let approvalRequests = [];
                    idsToApprove.forEach(externalId => {
                        const databaseId = fileIdMapping[externalId];
                        if (databaseId) {
                            approvalRequests.push(this.approveFile(integration.repositoryId, databaseId, effectiveCollectionId, targetResourceId));
                        } else {
                            console.warn(`No database ID found for external file ID: ${externalId}`);
                        }
                    });
                    await Promise.all(approvalRequests);

                    await this.tools.fetchAndUpdateQuota();

                    await this.getRepositories();

                    if (this.props.oncomplete) this.props.oncomplete();

                    // In reupload mode the host shows a single "Reupload Complete"
                    // confirmation, so skip this generic import-completed message
                    // (it would be redundant and worded for create-new).
                    if (!this.props.resourceid) {
                        modalService.show(
                            this.locale.t('upload_discoverycompleted'),
                            this.locale.t('upload_filesprocessing', { count })
                        );
                    }

                } catch (error) {
                    console.error('Failed to discover files:', error);
                    // Safely extract error message from various error structures
                    let errorMessage = 'Unknown error occurred';
                    if (error?.response?.data?.error?.message) {
                        errorMessage = error.response.data.error.message;
                    } else if (error?.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    }

                    // Check for quota limit error (same pattern as tools.js uploadFile)
                    if (this.tools.isQuotaError(errorMessage)) {
                        this.tools.showQuotaLimitModal();
                        return; // Exit early, quota modal will be shown
                    }

                    const modalService = await getModalService();
                    modalService.show(
                        this.locale.t('upload_discoveryfailed'),
                        this.locale.t('upload_failedtostartdiscovery') + ' ' + errorMessage
                    );
                }
            },

            async pollSyncJobUntilComplete(repositoryId, syncJobId, maxAttempts = 60, intervalMs = 2000) {
                const repositoryService = await getRepositoryService();
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    try {
                        const result = await repositoryService.getSyncJob(repositoryId, syncJobId);
                        const syncJob = result.syncJob;

                        console.log(`Polling sync job ${syncJobId} (attempt ${attempt + 1}/${maxAttempts}):`, syncJob.status);

                        if (syncJob.status === 'completed') {
                            console.log('Sync job completed successfully');
                            return syncJob;
                        }

                        if (syncJob.status === 'failed') {
                            throw new Error('Sync job failed: ' + (syncJob.errorMessage || 'Unknown error'));
                        }

                        // Wait before next poll
                        await new Promise(resolve => setTimeout(resolve, intervalMs));
                    } catch (error) {
                        if (error.message && error.message.includes('failed')) {
                            throw error;
                        }
                        console.error('Error polling sync job:', error);
                        throw new Error('Failed to check sync job status');
                    }
                }

                throw new Error('Sync job timed out after ' + maxAttempts + ' attempts');
            },

            async approveFile(repositoryId, fileId, collectionId, resourceId) {
                const api = await getApi();
                await api.post(
                    `/repository-integrations/${repositoryId}/files/${fileId}/action`,
                    {
                        action: 'approve',
                        ...(collectionId && { collectionId }),
                        // Reingest into an existing resource instead of creating a
                        // new one (METALARK-1132). Omitted for create-new flows.
                        ...(resourceId && { resourceId })
                    }
                );
            }
        }
    </script>
</upload-source-picker>
